import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { RoleRoute } from '@/routes/RoleRoute';
import { CompetitionRoleRoute } from '@/routes/CompetitionRoleRoute';
import { AnyCompetitionRoleRoute } from '@/routes/AnyCompetitionRoleRoute';

import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { CompetitionPickerPage } from '@/pages/CompetitionPickerPage';
import { HomeRoute } from '@/routes/HomeRoute';
import { CompetitionDetailsPage } from '@/pages/CompetitionDetailsPage';
import { SubmissionDetailsPage } from '@/pages/SubmissionDetailsPage';
import { UserGuidePage } from '@/pages/guide/UserGuidePage';
import { CompetitionLandingPage } from '@/pages/CompetitionLandingPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProfilePage } from '@/pages/ProfilePage';

import { UploadsPage } from '@/pages/competitor/UploadsPage';
import { ResultsPage } from '@/pages/competitor/ResultsPage';

import { OrganizerOverviewPage } from '@/pages/organizer/OrganizerOverviewPage';
import { OrganizerCompetitionsPage } from '@/pages/organizer/OrganizerCompetitionsPage';
import { OrganizerSetupPage } from '@/pages/organizer/OrganizerSetupPage';
import { OrganizerCategoriesPage } from '@/pages/organizer/OrganizerCategoriesPage';
import { OrganizerLandingPage } from '@/pages/organizer/OrganizerLandingPage';
import { OrganizerOrganizationPage } from '@/pages/organizer/OrganizerOrganizationPage';
import { OrganizerOrganizationPaymentsPage } from '@/pages/organizer/OrganizerOrganizationPaymentsPage';
import { OrganizerAwardsPage } from '@/pages/organizer/OrganizerAwardsPage';
import { OrganizerJudgesPage } from '@/pages/organizer/OrganizerJudgesPage';
import { OrganizerParticipantsPage } from '@/pages/organizer/OrganizerParticipantsPage';
import { OrganizerReportsPage } from '@/pages/organizer/OrganizerReportsPage';

import { JudgeScoringPage } from '@/pages/judge/JudgeScoringPage';
import { JudgeRankingPage } from '@/pages/judge/JudgeRankingPage';

import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminOrganizationsPage } from '@/pages/admin/AdminOrganizationsPage';
import { AdminLogsPage } from '@/pages/admin/AdminLogsPage';
import { AdminSettingsPage } from '@/pages/admin/AdminSettingsPage';

export function AppRouter() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* The announcement page of a competition is public: it is what gets shared. */}
      <Route path="/razpis/:competitionRef" element={<CompetitionLandingPage />} />
      <Route path="/navodila-za-uporabo/:page?" element={<UserGuidePage />} />

      <Route element={<ProtectedRoute />}>
        {/* Full-screen competition choice, outside the app shell. */}
        <Route path="/" element={<HomeRoute />} />
        <Route path="/competitions" element={<CompetitionPickerPage />} />
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/competitions/:competitionRef" element={<CompetitionDetailsPage />} />
          <Route path="/submissions/:submissionId" element={<SubmissionDetailsPage />} />
          {/* Account settings are for every signed-in user, whatever their roles. */}
          <Route path="/profile" element={<ProfilePage />} />

          {/* Competitor portal is not scoped to one specific competition (see /competitions/:id for that), so gated on "approved participant in at least one competition". */}
          <Route element={<AnyCompetitionRoleRoute allow={['participant']} />}>
            <Route path="/uploads" element={<UploadsPage />} />
            <Route path="/results" element={<ResultsPage />} />
          </Route>

          <Route element={<AnyCompetitionRoleRoute allow={['manage']} />}>
            <Route path="/organizer" element={<OrganizerOverviewPage />} />
            <Route path="/organizer/competitions" element={<OrganizerCompetitionsPage />} />
            <Route path="/organizer/reports" element={<OrganizerReportsPage />} />
            {/* Organization-wide settings; the backend checks membership of that organization. */}
            <Route path="/organizer/organizations/:organizationId" element={<OrganizerOrganizationPage />} />
            <Route path="/organizer/organizations/:organizationId/payments" element={<OrganizerOrganizationPaymentsPage />} />
          </Route>

          {/* These take :competitionRef in the URL - guarded per-competition, not just "manages something somewhere". */}
          <Route element={<CompetitionRoleRoute allow={['manage']} />}>
            <Route path="/organizer/competitions/:competitionRef/setup" element={<OrganizerSetupPage />} />
            <Route path="/organizer/competitions/:competitionRef/categories" element={<OrganizerCategoriesPage />} />
            <Route path="/organizer/competitions/:competitionRef/landing" element={<OrganizerLandingPage />} />
            <Route path="/organizer/competitions/:competitionRef/awards" element={<OrganizerAwardsPage />} />
            <Route path="/organizer/competitions/:competitionRef/judges" element={<OrganizerJudgesPage />} />
            <Route path="/organizer/competitions/:competitionRef/participants" element={<OrganizerParticipantsPage />} />
          </Route>

          <Route element={<AnyCompetitionRoleRoute allow={['judge']} />}>
            <Route path="/judge/scoring" element={<JudgeScoringPage />} />
            <Route path="/judge/ranking" element={<JudgeRankingPage />} />
          </Route>

          {/* Cross-org/platform-wide admin views - SuperAdmin only. Former global OrganizationAdmins manage their own org through the organizer UI instead. */}
          <Route element={<RoleRoute allow={['SuperAdmin']} />}>
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
            <Route path="/admin/logs" element={<AdminLogsPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
